import { InstrumentReadings } from '../systems/Instruments';
import { NavReadings } from '../systems/Navigation';
import { ControlSurfaces } from '../systems/Controls';
import { FitRect, backingResolution } from '../core/Letterbox';
import * as G from './gauges';

/**
 * Lays out and renders the C172S instrument panel onto a 2D canvas.
 * The upper region (windscreen) is left transparent so the 3D outside view
 * shows through; the dashboard and gauges are drawn below the glareshield.
 *
 * All layout math is done in a FIXED logical coordinate space — always
 * exactly LOGICAL_W x LOGICAL_H, a constant 16:9 canvas that never changes,
 * regardless of the actual browser window size. Only a single uniform scale
 * factor (logical -> backing-buffer pixels) is re-derived on resize; every
 * gauge position, radius, and font size is computed from these two constants
 * and is therefore identical at every window size. This is what makes the
 * layout genuinely fixed (arcs/fonts/spacing never drift as you resize the
 * window) while still being easy to adjust: change a number here and it has
 * the same, predictable effect at every screen size — no need to re-test
 * every window shape to see if a tweak "sticks".
 *
 * (Before this, resize() re-derived the logical drawing size from the actual
 * FitRect's CSS pixel dimensions each time, which — despite the fixed 16:9
 * *aspect ratio* — still changed in absolute terms on every resize. A few
 * hardcoded pixel constants sprinkled through gauges.ts (bezel border width,
 * needle-hub radius, etc.) don't scale with that, so the effective on-screen
 * proportions could drift slightly between window sizes.)
 */
const LOGICAL_W = 1920;
const LOGICAL_H = 1080;

export class Panel {
  private ctx: CanvasRenderingContext2D;
  obsCourse = 240; // aligned with the runway ILS by default
  headingBug = 240;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d')!;
  }

  /** Re-derive the canvas backing buffer + logical->backing scale from a FitRect.
   * The logical drawing space itself (LOGICAL_W x LOGICAL_H) never changes. */
  resize(rect: FitRect): void {
    const { w, h } = backingResolution(rect);
    this.canvas.width = w;
    this.canvas.height = h;
    // FitRect is always exactly 16:9 (same as LOGICAL_W:LOGICAL_H), so this is
    // a single uniform scale — never any stretch/distortion.
    this.ctx.setTransform(w / LOGICAL_W, 0, 0, h / LOGICAL_H, 0, 0);
  }

  /** y-fraction of the panel where the glareshield/dashboard starts: outside
   * view gets 2/5 of the screen, dashboard gets the remaining 3/5. */
  get glareshieldFrac(): number {
    return 0.4;
  }

  draw(nav: NavReadings, controls: ControlSurfaces): void {
    const ctx = this.ctx;
    const W = LOGICAL_W;
    const H = LOGICAL_H;
    ctx.clearRect(0, 0, W, H);

    const dashTop = H * this.glareshieldFrac;

    // Coaming (glareshield lip)
    const grad = ctx.createLinearGradient(0, dashTop - 24, 0, dashTop + 8);
    grad.addColorStop(0, 'rgba(20,20,22,0)');
    grad.addColorStop(0.5, '#1a1a1c');
    grad.addColorStop(1, '#111');
    ctx.fillStyle = grad;
    ctx.fillRect(0, dashTop - 24, W, 32);

    // Dashboard panel
    ctx.fillStyle = '#3a3d40';
    ctx.fillRect(0, dashTop, W, H - dashTop);
    // subtle panel texture line
    ctx.fillStyle = 'rgba(0,0,0,0.15)';
    ctx.fillRect(0, dashTop, W, 3);

    // --- Instrument layout -------------------------------------------------
    // Two rows of five columns, centred. Since W:H is always exactly 16:9
    // (the letterboxed FitRect), this only ever has to fit one aspect ratio.
    // Layout grid (columns left→right): tach, ASI/TC, AI/DG, ALT/VSI, CDI.
    const availH = H - dashTop;
    const statusH = clamp(availH * 0.16, 64, 110); // back to a compact corner widget
    const topMargin = availH * 0.03;
    const gaugeTop = dashTop + topMargin;
    const gaugeH = H - statusH - gaugeTop;

    // Radius is bounded by BOTH the vertical space (must hold two rows plus a
    // little label overhang) and the horizontal space (five columns wide).
    // blockH(r) = rowSpacing(2.35r) + 2r (gauge diameters) + 0.6r (label
    // overhang) = 4.95r exactly.
    // colWidth(r): 5 columns spaced 2.5r apart plus a radius of margin on each
    // end = 4*2.5r + 2r = 12r.
    // Divisors here (9.1, 3.75) are the original (12, 4.95) each divided by
    // ~1.32 — a further ~10% bump on top of the earlier +20%, whichever
    // bound (width or height) is binding.
    const heightBudget = gaugeH * 0.97; // small safety margin for label overhang
    const r = Math.max(24, Math.min((W * 0.98) / 9.1, heightBudget / 3.75));
    // Edge-to-edge gap between adjacent round gauges — the same gap is now
    // used both horizontally and vertically (previously rows used 0.35r and
    // columns used 0.5r), shrunk to 2/3 of the original row gap so the whole
    // six-pack sits noticeably tighter together.
    const edgeGap = r * 0.35 * (2 / 3);
    const spacing = 2 * r + edgeGap;
    const colGap = spacing;
    const cx = W / 2;
    const rowSpacing = spacing;

    // Vertically centre the two-row block within the gauge area.
    const blockH = rowSpacing + 2 * r + r * 0.6; // + overhang for VSI labels
    const blockTop = gaugeTop + Math.max(0, (gaugeH - blockH) / 2);
    const row1 = blockTop + r;
    const row2 = row1 + rowSpacing;

    // Column centres
    const cTach = cx - 2 * colGap;
    const cLeft = cx - colGap;
    const cMid = cx;
    const cRight = cx + colGap;
    const cCDI = cx + 2 * colGap;

    // Row 1: Airspeed, Attitude, Altimeter, and the CDI on the right
    G.drawAirspeed(ctx, cLeft, row1, r, this.iasVal);
    G.drawAttitude(ctx, cMid, row1, r, this.pitchVal, this.rollVal);
    G.drawAltimeter(ctx, cRight, row1, r, this.altVal, this.altSet);
    G.drawCDI(ctx, cCDI, row1, r, nav, this.obsCourse);

    // Row 2: Turn coordinator, Heading, VSI, and the tach on the left
    G.drawTurnCoordinator(ctx, cLeft, row2, r, this.turnVal, this.slipVal);
    G.drawHeading(ctx, cMid, row2, r, this.hdgVal, this.headingBug);
    G.drawVSI(ctx, cRight, row2, r, this.vsiVal);
    G.drawTach(ctx, cTach, row2, r, this.rpmVal); // same size as the rest of the six-pack

    // THR/MIX: its own compact instrument, dropped into row 2's rightmost
    // grid slot (the CDI column, which row 2 otherwise leaves empty) — right
    // of the VSI, matching its diameter for height and a bit wider.
    const thrMixH = 2 * r;
    const thrMixW = thrMixH * 1.25;
    this.drawThrMix(ctx, cCDI - thrMixW / 2, row2 - r, thrMixW, thrMixH, controls);

    this.drawStatusStrip(ctx, W, H, statusH, controls);
  }

  // Cached scalar reads set by update() to avoid recomputation clutter above.
  private iasVal = 0; private pitchVal = 0; private rollVal = 0;
  private altVal = 0; private altSet = 29.92; private turnVal = 0; private slipVal = 0;
  private hdgVal = 0; private vsiVal = 0; private rpmVal = 0;
  private fuelL = 0; private oat = 15;

  /** Called each frame before draw() to load the readings. */
  load(r: InstrumentReadings): void {
    this.iasVal = r.ias; this.pitchVal = r.pitchDeg; this.rollVal = r.rollDeg;
    this.altVal = r.altitude; this.altSet = r.altimeterSetting;
    this.turnVal = r.turnRate; this.slipVal = r.slip; this.hdgVal = r.headingDeg;
    this.vsiVal = r.vsi; this.rpmVal = r.rpm; this.fuelL = r.fuelL; this.oat = r.oatC;
  }

  /** Throttle/mixture bar-graph instrument, sized to match the VSI's diameter
   * (height) and a bit wider — sits immediately right of the VSI. */
  private drawThrMix(
    ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c: ControlSurfaces,
  ): void {
    ctx.fillStyle = 'rgba(10,12,14,0.85)'; ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = '#2b2f33'; ctx.strokeRect(x, y, w, h);

    const barW = Math.min(30, w * 0.28);
    const barTop = y + h * 0.16, barBottom = y + h - 8;
    const bar = (bx: number, labelText: string, frac: number, color: string) => {
      ctx.fillStyle = '#9aa'; ctx.font = '12px ui-monospace, monospace'; ctx.textAlign = 'center';
      ctx.fillText(labelText, bx + barW / 2, y + 14);
      ctx.fillStyle = '#222'; ctx.fillRect(bx, barTop, barW, barBottom - barTop);
      const filledH = (barBottom - barTop) * Math.max(0, Math.min(1, frac));
      ctx.fillStyle = color; ctx.fillRect(bx, barBottom - filledH, barW, filledH);
    };
    const gap = w * 0.14;
    const x0 = x + (w - (barW * 2 + gap)) / 2;
    bar(x0, 'THR', c.throttle, '#3fd06a');
    bar(x0 + barW + gap, 'MIX', c.mixture, '#d0a53f');
  }

  private drawStatusStrip(
    ctx: CanvasRenderingContext2D, W: number, H: number, statusH: number, c: ControlSurfaces,
  ): void {
    // Two small separate instruments tucked in the bottom-right corner:
    // TRIM/OAT/FUEL (text readout) and the flap lever, each with its own box.
    const boxH = Math.min(statusH - 10, 84);
    const y = H - statusH + (statusH - boxH) / 2;

    // Flap lever, rightmost.
    const flapBoxW = Math.min(W * 0.06, 90);
    const flapBoxX = W - flapBoxW - W * 0.015;
    ctx.fillStyle = 'rgba(10,12,14,0.85)'; ctx.fillRect(flapBoxX, y, flapBoxW, boxH);
    ctx.strokeStyle = '#2b2f33'; ctx.strokeRect(flapBoxX, y, flapBoxW, boxH);
    const leverW = 34, leverH = boxH - 12; // leaves room for the handle's overhang to the right
    G.drawFlapLever(ctx, flapBoxX + 8, y + 6, leverW, leverH, c.flapDetent);

    // TRIM/OAT/FUEL, just left of the flap lever.
    const trimBoxW = Math.min(W * 0.09, 130);
    const trimBoxX = flapBoxX - trimBoxW - W * 0.012;
    ctx.fillStyle = 'rgba(10,12,14,0.85)'; ctx.fillRect(trimBoxX, y, trimBoxW, boxH);
    ctx.strokeStyle = '#2b2f33'; ctx.strokeRect(trimBoxX, y, trimBoxW, boxH);
    ctx.fillStyle = '#cfe'; ctx.font = '11px ui-monospace, monospace'; ctx.textAlign = 'left';
    const tx = trimBoxX + 10;
    const trimPct = (c.trim * 100).toFixed(0);
    ctx.fillText(`TRIM ${trimPct}%`, tx, y + 16);
    ctx.fillText(`OAT ${this.oat.toFixed(0)}°C`, tx, y + 32);
    ctx.fillText(`FUEL ${this.fuelL.toFixed(0)} L`, tx, y + 48);
    ctx.fillText(c.brakes > 0 || c.brakeLeft > 0 || c.brakeRight > 0 ? 'BRAKES' : '', tx, y + 64);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
